import React, { FC, useContext, useEffect, useState } from 'react';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { Chat, History, MarkChatRead } from '@mui/icons-material';
import ReviewReply from './ReviewReply';
import Comment from '../Comment/Comment';
import ReviewReplyGMB from './ReviewReplyGMB';
import TabPanel from '../TabPanel/TabPanel';
import ReviewNew from './ReviewNew';
import Helper from '../../shared/helpers/Helper';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../providers/AuthProvider';
import { ReviewStatus } from './ReviewStatus';
import { IReviewDateFormat, IReviewNewProps } from '../../shared/interfaces/IReviewNew';
import CommentingService from '../../services/CommentingService';
import DeleteIcon from '@mui/icons-material/Delete';
import Confirm from '../Confirm/Confirm';
import { IReviewComment } from '../../shared/interfaces/IReviewComment';
import { IReviewMetadata } from '../../services/CommentAiService';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import GroupTagChip from '../GroupTagChip/GroupTagChip';

interface Props extends IReviewNewProps {
  input: string;
  hide?: boolean;
  dateFormat?: IReviewDateFormat;
  noReviewText: boolean;
  rating: number;
  reviewMeta: IReviewMetadata;
}

const ReviewFooter: FC<Props> = ({
  input,
  hide,
  communityManagement,
  commentingModule,
  statusModule,
  reviewEntry,
  dateFormat,
  noReviewText,
  rating,
  reviewMeta,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [commentExpanded, setCommentExpanded] = useState<boolean>(false);
  const [histories, setHistories] = useState<any[] | undefined>(reviewEntry.history);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  const { hasRole } = useContext(AuthContext);

  const [currentTab, setCurrentTab] = useState<number>(
    reviewEntry?.reviewComment?.expanded || false ? 0 : -1
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const deleteHistory = async (reviewId: string, historyIndex: number) => {
    if (histories) {
      await CommentingService.deleteReviewHistory(reviewId, String(historyIndex));
      const newHistories = histories;
      newHistories.splice(historyIndex, 1);
      setHistories(newHistories);
    }
    setDeleteIndex(-1);
  };

  if (hide || !communityManagement) {
    return null;
  }

  function onCommentSaveSuccess(reply: IReviewComment) {
    setCurrentTab(-1);
    if (typeof commentingModule === 'object') {
      commentingModule.onCommentSaved(reviewEntry.id, reply);
    }
  }

  const showHistory =
    hasRole('can-use-reviewhistory') && reviewEntry.history && reviewEntry.history.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          borderBottom: currentTab === -1 ? 0 : 1,
          borderColor: 'divider',
          ml: theme.spacing(2),
          mr: theme.spacing(2),
        }}
      >
        <Tabs
          value={currentTab > 0 ? currentTab : false}
          onChange={handleTabChange}
        >
          {showHistory && (
            <Tab
              aria-label="Historie anzeigen"
              value={3}
              icon={<History />}
            />
          )}
          {commentingModule && (
            <Tab
              aria-label={t('show_comment')}
              value={0}
              icon={reviewEntry.reply?.comment ? <MarkChatRead /> : <Chat />}
            />
          )}
          {!commentingModule && reviewEntry.reply?.comment && (
            <Tab
              aria-label={t('show_comment')}
              value={1}
              icon={<MarkChatRead />}
            />
          )}
        </Tabs>
      </Box>
      <Box
        sx={{
          ml: theme.spacing(1),
          mr: theme.spacing(1),
        }}
      >
        {commentingModule && (
          <TabPanel
            value={currentTab}
            index={0}
            onCloseClick={() => setCurrentTab(-1)}
          >
            {reviewEntry.reply?.comment && (
              <>
                <Comment
                  companyComment={reviewEntry.reply}
                  dateFormat={dateFormat}
                  sx={{ mt: theme.spacing(2) }}
                />
              </>
            )}
            {reviewEntry.platform === 'google' &&
            reviewEntry.review.reviewId &&
            reviewEntry.gmbLocationId ? (
              <ReviewReplyGMB
                id={reviewEntry.id}
                reviewId={reviewEntry.review.reviewId}
                locationId={reviewEntry.gmbLocationId}
                input={input || ''}
                noReviewText={noReviewText}
                reviewComment={reviewEntry.reviewComment}
                communityManagement={communityManagement}
                onSaveSuccess={onCommentSaveSuccess}
                platform={reviewEntry.platform}
                reviewMetadata={reviewMeta}
              />
            ) : (
              <>
                <ReviewReply
                  // The Mongo _id, never review.reviewId: this saves the comment status through
                  // PATCH /portal/comments/reviews/comment, which unmarshals it into a primitive.ObjectID.
                  // Only google reviews carry review.reviewId, so every other platform already resolved to
                  // id here — this branch is newly reachable for a google review whose location did not
                  // resolve, and the Google id would fail silently there (772363).
                  reviewId={reviewEntry.id ?? ''}
                  reviewComment={reviewEntry.reviewComment}
                  communityManagement={communityManagement}
                  input={input || ''}
                  onSaveSuccess={onCommentSaveSuccess}
                  reviewMetadata={reviewMeta}
                  noReviewText={noReviewText}
                />
              </>
            )}
          </TabPanel>
        )}
        {!commentingModule && reviewEntry.reply?.comment && (
          <TabPanel
            value={currentTab}
            index={1}
            onCloseClick={() => setCurrentTab(-1)}
          >
            <Comment
              companyComment={reviewEntry.reply}
              sx={{ mt: theme.spacing(2) }}
              dateFormat={dateFormat}
            />
          </TabPanel>
        )}
        {showHistory && (
          <TabPanel
            value={currentTab}
            index={3}
            onCloseClick={() => setCurrentTab(-1)}
          >
            {histories &&
              histories.map((item: any, i: number) => {
                return (
                  <Box key={i}>
                    <Confirm
                      text={t('delete_confirm')}
                      open={deleteIndex > -1}
                      onCancel={() => setDeleteIndex(-1)}
                      onOk={async () => await deleteHistory(reviewEntry.id, deleteIndex)}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'left', gap: '1em' }}>
                      <div>
                        <span>{t('created_on')} </span>
                        <strong>{Helper.getFormattedDate(item.timestamp, true)}</strong>
                      </div>
                      {item.scrape_time && (
                        <div>
                          <span>{t('found_on')} </span>
                          <strong>{Helper.getFormattedDate(item.scrape_time, true)}</strong>
                        </div>
                      )}
                      {hasRole('can-delete-reviewhistory') && (
                        <div onClick={() => setDeleteIndex(i)}>
                          <DeleteIcon
                            color="error"
                            fontSize="small"
                          />
                        </div>
                      )}
                    </Box>
                    <ReviewNew
                      reviews={[
                        {
                          id: reviewEntry.review.reviewId ?? reviewEntry.id ?? '',
                          platform: reviewEntry.platform || '',
                          identifier: '',
                          company: '',
                          createdAt: '',
                          updatedAt: '',
                          firstScrapedAt: '',
                          reviewerRole: '',
                          lastScrapedAt: '',
                          review: item,
                          timestamp: item.timestamp,
                          scrapeCount: 0,
                          note: '',
                          deleted: false,
                          deletion: false,
                          currentStatus: '',
                          reply: null,
                        },
                      ]}
                      status={[]}
                      monitoring={[]}
                      commentingModule={false}
                      hideHeader={true}
                      hideFooter={true}
                      location={[]}
                      deletionModule={false}
                    />
                  </Box>
                );
              })}
          </TabPanel>
        )}
        {statusModule && (
          <ReviewStatus
            currentStatus={reviewEntry.currentStatus ?? ''}
            theme={theme}
            status={reviewEntry.status ?? []}
            id={reviewEntry.review.reviewId ?? reviewEntry.id ?? ''}
          ></ReviewStatus>
        )}
      </Box>
      {reviewEntry.tags && reviewEntry.tags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 0.5, mt: 0.5 }}>
          {reviewEntry.tags?.map((tag) => (
            <GroupTagChip
              key={tag.id}
              tag={tag}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ReviewFooter;
